// src/pages/admin/Comments.jsx
import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, XCircle, Trash2, MessageSquare } from "lucide-react";
import AdminLayout from "./AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { formatDate } from "../../lib/utils";
import {
  listAdminComments,
  approveComment,
  rejectComment,
  deleteComment,
} from "../../lib/adminApi";

const getCommentText = (c) => c?.content ?? c?.message ?? c?.comment ?? "";

const isApproved = (c) => {
  if (typeof c?.approved === "boolean") return c.approved;
  if (typeof c?.is_approved === "boolean") return c.is_approved;
  if (typeof c?.status === "string") return c.status.toLowerCase() === "approved";
  return false;
};

const statusLabel = (c) => (isApproved(c) ? "Approved" : "Pending");

const Comments = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending"); // default matches your API
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      // your API supports status param
      const data =
        statusFilter === "all"
          ? await listAdminComments("all")
          : await listAdminComments(statusFilter);

      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load comments:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;

    return items.filter((c) => {
      const haystack = [c?.post_title, c?.name, c?.email, getCommentText(c)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, query]);

  const runAction = async (id, action) => {
    if (!id) return;
    try {
      setBusyId(id);
      await action(id);
      await load();
    } catch (err) {
      console.error("Comment action failed:", err);
      alert(err?.message || "Action failed");
    } finally {
      setBusyId("");
    }
  };

  return (
    <AdminLayout title="Comments Moderation" onRefresh={load}>
      <div className="mb-4 flex flex-wrap items-center gap-3 justify-between">
        <h2 className="text-xl font-bold text-stone-900">Manage Comments</h2>
      </div>

      <Card className="rounded-2xl border-stone-200 mb-4">
        <CardContent className="p-4">
          <div className="grid md:grid-cols-[1fr_220px] gap-3">
            <Input
              placeholder="Search by post title, name, email, or comment text..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <select
              className="h-10 rounded-md border px-3 bg-white"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending only</option>
              <option value="approved">Approved only</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-stone-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-stone-600">Loading comments...</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-stone-600">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 text-stone-400" />
              No comments found.
            </div>
          ) : (
            <div className="divide-y">
              {filtered.map((c) => {
                const approved = isApproved(c);
                const busy = busyId === c.id;

                return (
                  <div key={c.id} className="p-4 md:p-5 flex flex-col gap-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-stone-900 truncate">
                          {c?.post_title || "Untitled Post"}
                        </p>
                        <p className="text-sm text-stone-500">
                          {c?.name || "Anonymous"}
                          {c?.email ? ` • ${c.email}` : ""} • {formatDate(c?.created_at)}
                        </p>
                      </div>

                      <span
                        className={[
                          "text-xs font-medium px-2 py-1 rounded-full",
                          approved
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-amber-100 text-amber-800",
                        ].join(" ")}
                      >
                        {statusLabel(c)}
                      </span>
                    </div>

                    <p className="text-stone-700 whitespace-pre-wrap">
                      {getCommentText(c) || "-"}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {!approved ? (
                        <Button
                          className="bg-emerald-600 hover:bg-emerald-700 rounded-xl"
                          onClick={() => runAction(c.id, approveComment)}
                          disabled={busy}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          {busy ? "Processing..." : "Approve"}
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          className="rounded-xl"
                          onClick={() => runAction(c.id, rejectComment)}
                          disabled={busy}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          {busy ? "Processing..." : "Unapprove"}
                        </Button>
                      )}

                      <Button
                        variant="outline"
                        className="rounded-xl"
                        onClick={() => runAction(c.id, deleteComment)}
                        disabled={busy}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {busy ? "Deleting..." : "Delete"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default Comments;
